import { gql } from '@apollo/client';
export const getContactsFromConversation = conversation => {
  return conversation.associations.conversationToContact.edges.map(edge => edge.node);
};
export const getConversationsFromTicket = ticket => {
  const conversationEdges = ticket.associations.ticketToConversationV2.edges;
  return conversationEdges.map(edge => edge.node);
};
export const getThreadIdsFromTicket = ticket => {
  const conversations = getConversationsFromTicket(ticket);
  return conversations.reduce((acc, conversation) => {
    const threadIdObject = conversation.defaultProperties.hs_thread_id;
    if (threadIdObject) {
      acc.push(threadIdObject.value);
    }
    return acc;
  }, []);
};

/**
 * Returns the threadIds of the conversations on the ticket sorted by the latest message timestamp.
 *
 * This currently is only used as the fallback method for getting the threadId when the threadId
 * is not available in the viewMembersList on initial help desk load
 */
export const getSortedThreadIdsFromTicketBasedOnLatestMessageTimestamp = ticket => {
  const conversations = getConversationsFromTicket(ticket);
  if (conversations.length === 0) {
    return [];
  }
  if (conversations.length === 1) {
    const threadIdObject = conversations[0].defaultProperties.hs_thread_id;
    return threadIdObject ? [threadIdObject.value] : [];
  }

  /**
   * In most cases, the performance of the sort function is acceptable because at the moment we only
   * fetch top 10 associated conversations on ticket.
   * If we ever change the logic and fetch a lot more conversations, there might be a performance
   * implication and we should prioritize the BE work mentioned above first
   */
  return conversations.sort((cvA, cvB) => {
    var _cvA$defaultPropertie, _cvA$defaultPropertie2, _cvB$defaultPropertie, _cvB$defaultPropertie2;
    const cvALatestMessageReceivedAt = (_cvA$defaultPropertie = (_cvA$defaultPropertie2 = cvA.defaultProperties.hs_last_message_at) === null || _cvA$defaultPropertie2 === void 0 ? void 0 : _cvA$defaultPropertie2.value) !== null && _cvA$defaultPropertie !== void 0 ? _cvA$defaultPropertie : 0;
    const cvBLatestMessageReceivedAt = (_cvB$defaultPropertie = (_cvB$defaultPropertie2 = cvB.defaultProperties.hs_last_message_at) === null || _cvB$defaultPropertie2 === void 0 ? void 0 : _cvB$defaultPropertie2.value) !== null && _cvB$defaultPropertie !== void 0 ? _cvB$defaultPropertie : 0;
    return cvBLatestMessageReceivedAt - cvALatestMessageReceivedAt; // Sort in descending order (newest first)
  }).reduce((acc, conversation) => {
    const threadIdObject = conversation.defaultProperties.hs_thread_id;
    if (threadIdObject) {
      acc.push(threadIdObject.value);
    }
    return acc;
  }, []);
};
export const TicketToConversationAssociationFragmentV2 = ("__gql__", "{\"kind\":\"Document\",\"definitions\":[{\"kind\":\"FragmentDefinition\",\"name\":{\"kind\":\"Name\",\"value\":\"TicketToConversationAssociationFragmentV2\"},\"typeCondition\":{\"kind\":\"NamedType\",\"name\":{\"kind\":\"Name\",\"value\":\"CrmObject\"}},\"selectionSet\":{\"kind\":\"SelectionSet\",\"selections\":[{\"kind\":\"InlineFragment\",\"typeCondition\":{\"kind\":\"NamedType\",\"name\":{\"kind\":\"Name\",\"value\":\"Ticket\"}},\"selectionSet\":{\"kind\":\"SelectionSet\",\"selections\":[{\"kind\":\"Field\",\"name\":{\"kind\":\"Name\",\"value\":\"associations\"},\"selectionSet\":{\"kind\":\"SelectionSet\",\"selections\":[{\"kind\":\"Field\",\"name\":{\"kind\":\"Name\",\"value\":\"ticketToConversationV2\"},\"arguments\":[{\"kind\":\"Argument\",\"name\":{\"kind\":\"Name\",\"value\":\"first\"},\"value\":{\"kind\":\"IntValue\",\"value\":\"10\"}}],\"selectionSet\":{\"kind\":\"SelectionSet\",\"selections\":[{\"kind\":\"Field\",\"name\":{\"kind\":\"Name\",\"value\":\"edges\"},\"selectionSet\":{\"kind\":\"SelectionSet\",\"selections\":[{\"kind\":\"Field\",\"name\":{\"kind\":\"Name\",\"value\":\"node\"},\"selectionSet\":{\"kind\":\"SelectionSet\",\"selections\":[{\"kind\":\"Field\",\"name\":{\"kind\":\"Name\",\"value\":\"associations\"},\"selectionSet\":{\"kind\":\"SelectionSet\",\"selections\":[{\"kind\":\"Field\",\"name\":{\"kind\":\"Name\",\"value\":\"conversationToContact\"},\"arguments\":[{\"kind\":\"Argument\",\"name\":{\"kind\":\"Name\",\"value\":\"first\"},\"value\":{\"kind\":\"IntValue\",\"value\":\"10\"}}],\"selectionSet\":{\"kind\":\"SelectionSet\",\"selections\":[{\"kind\":\"Field\",\"name\":{\"kind\":\"Name\",\"value\":\"edges\"},\"selectionSet\":{\"kind\":\"SelectionSet\",\"selections\":[{\"kind\":\"Field\",\"name\":{\"kind\":\"Name\",\"value\":\"node\"},\"selectionSet\":{\"kind\":\"SelectionSet\",\"selections\":[{\"kind\":\"Field\",\"name\":{\"kind\":\"Name\",\"value\":\"id\"}},{\"kind\":\"Field\",\"name\":{\"kind\":\"Name\",\"value\":\"defaultProperties\"},\"selectionSet\":{\"kind\":\"SelectionSet\",\"selections\":[{\"kind\":\"Field\",\"name\":{\"kind\":\"Name\",\"value\":\"firstname\"},\"selectionSet\":{\"kind\":\"SelectionSet\",\"selections\":[{\"kind\":\"Field\",\"name\":{\"kind\":\"Name\",\"value\":\"value\"}}]}},{\"kind\":\"Field\",\"name\":{\"kind\":\"Name\",\"value\":\"email\"},\"selectionSet\":{\"kind\":\"SelectionSet\",\"selections\":[{\"kind\":\"Field\",\"name\":{\"kind\":\"Name\",\"value\":\"value\"}}]}},{\"kind\":\"Field\",\"name\":{\"kind\":\"Name\",\"value\":\"lastname\"},\"selectionSet\":{\"kind\":\"SelectionSet\",\"selections\":[{\"kind\":\"Field\",\"name\":{\"kind\":\"Name\",\"value\":\"value\"}}]}}]}}]}}]}}]}}]}},{\"kind\":\"Field\",\"name\":{\"kind\":\"Name\",\"value\":\"id\"}},{\"kind\":\"Field\",\"name\":{\"kind\":\"Name\",\"value\":\"defaultProperties\"},\"selectionSet\":{\"kind\":\"SelectionSet\",\"selections\":[{\"kind\":\"Field\",\"name\":{\"kind\":\"Name\",\"value\":\"hs_thread_id\"},\"selectionSet\":{\"kind\":\"SelectionSet\",\"selections\":[{\"kind\":\"Field\",\"name\":{\"kind\":\"Name\",\"value\":\"value\"}},{\"kind\":\"Field\",\"name\":{\"kind\":\"Name\",\"value\":\"id\"}}]}},{\"kind\":\"Field\",\"name\":{\"kind\":\"Name\",\"value\":\"hs_conversation_channel\"},\"selectionSet\":{\"kind\":\"SelectionSet\",\"selections\":[{\"kind\":\"Field\",\"name\":{\"kind\":\"Name\",\"value\":\"id\"}},{\"kind\":\"Field\",\"name\":{\"kind\":\"Name\",\"value\":\"value\"}}]}},{\"kind\":\"Field\",\"name\":{\"kind\":\"Name\",\"value\":\"hs_name\"},\"selectionSet\":{\"kind\":\"SelectionSet\",\"selections\":[{\"kind\":\"Field\",\"name\":{\"kind\":\"Name\",\"value\":\"id\"}},{\"kind\":\"Field\",\"name\":{\"kind\":\"Name\",\"value\":\"value\"}}]}},{\"kind\":\"Field\",\"name\":{\"kind\":\"Name\",\"value\":\"hs_inbox_id\"},\"selectionSet\":{\"kind\":\"SelectionSet\",\"selections\":[{\"kind\":\"Field\",\"name\":{\"kind\":\"Name\",\"value\":\"id\"}},{\"kind\":\"Field\",\"name\":{\"kind\":\"Name\",\"value\":\"value\"}}]}},{\"kind\":\"Field\",\"name\":{\"kind\":\"Name\",\"value\":\"hs_originating_channel_instance_id\"},\"selectionSet\":{\"kind\":\"SelectionSet\",\"selections\":[{\"kind\":\"Field\",\"name\":{\"kind\":\"Name\",\"value\":\"id\"}},{\"kind\":\"Field\",\"name\":{\"kind\":\"Name\",\"value\":\"value\"}}]}},{\"kind\":\"Field\",\"name\":{\"kind\":\"Name\",\"value\":\"hs_started_at\"},\"selectionSet\":{\"kind\":\"SelectionSet\",\"selections\":[{\"kind\":\"Field\",\"name\":{\"kind\":\"Name\",\"value\":\"id\"}},{\"kind\":\"Field\",\"name\":{\"kind\":\"Name\",\"value\":\"value\"}}]}},{\"kind\":\"Field\",\"name\":{\"kind\":\"Name\",\"value\":\"hs_last_message_at\"},\"selectionSet\":{\"kind\":\"SelectionSet\",\"selections\":[{\"kind\":\"Field\",\"name\":{\"kind\":\"Name\",\"value\":\"id\"}},{\"kind\":\"Field\",\"name\":{\"kind\":\"Name\",\"value\":\"value\"}}]}}]}}]}}]}}]}}]}}]}}]}}]}", {
  kind: "Document",
  definitions: [{
    kind: "FragmentDefinition",
    name: {
      kind: "Name",
      value: "TicketToConversationAssociationFragmentV2"
    },
    typeCondition: {
      kind: "NamedType",
      name: {
        kind: "Name",
        value: "CrmObject"
      }
    },
    selectionSet: {
      kind: "SelectionSet",
      selections: [{
        kind: "InlineFragment",
        typeCondition: {
          kind: "NamedType",
          name: {
            kind: "Name",
            value: "Ticket"
          }
        },
        selectionSet: {
          kind: "SelectionSet",
          selections: [{
            kind: "Field",
            name: {
              kind: "Name",
              value: "associations"
            },
            selectionSet: {
              kind: "SelectionSet",
              selections: [{
                kind: "Field",
                name: {
                  kind: "Name",
                  value: "ticketToConversationV2"
                },
                arguments: [{
                  kind: "Argument",
                  name: {
                    kind: "Name",
                    value: "first"
                  },
                  value: {
                    kind: "IntValue",
                    value: "10"
                  }
                }],
                selectionSet: {
                  kind: "SelectionSet",
                  selections: [{
                    kind: "Field",
                    name: {
                      kind: "Name",
                      value: "edges"
                    },
                    selectionSet: {
                      kind: "SelectionSet",
                      selections: [{
                        kind: "Field",
                        name: {
                          kind: "Name",
                          value: "node"
                        },
                        selectionSet: {
                          kind: "SelectionSet",
                          selections: [{
                            kind: "Field",
                            name: {
                              kind: "Name",
                              value: "associations"
                            },
                            selectionSet: {
                              kind: "SelectionSet",
                              selections: [{
                                kind: "Field",
                                name: {
                                  kind: "Name",
                                  value: "conversationToContact"
                                },
                                arguments: [{
                                  kind: "Argument",
                                  name: {
                                    kind: "Name",
                                    value: "first"
                                  },
                                  value: {
                                    kind: "IntValue",
                                    value: "10"
                                  }
                                }],
                                selectionSet: {
                                  kind: "SelectionSet",
                                  selections: [{
                                    kind: "Field",
                                    name: {
                                      kind: "Name",
                                      value: "edges"
                                    },
                                    selectionSet: {
                                      kind: "SelectionSet",
                                      selections: [{
                                        kind: "Field",
                                        name: {
                                          kind: "Name",
                                          value: "node"
                                        },
                                        selectionSet: {
                                          kind: "SelectionSet",
                                          selections: [{
                                            kind: "Field",
                                            name: {
                                              kind: "Name",
                                              value: "id"
                                            }
                                          }, {
                                            kind: "Field",
                                            name: {
                                              kind: "Name",
                                              value: "defaultProperties"
                                            },
                                            selectionSet: {
                                              kind: "SelectionSet",
                                              selections: [{
                                                kind: "Field",
                                                name: {
                                                  kind: "Name",
                                                  value: "firstname"
                                                },
                                                selectionSet: {
                                                  kind: "SelectionSet",
                                                  selections: [{
                                                    kind: "Field",
                                                    name: {
                                                      kind: "Name",
                                                      value: "value"
                                                    }
                                                  }]
                                                }
                                              }, {
                                                kind: "Field",
                                                name: {
                                                  kind: "Name",
                                                  value: "email"
                                                },
                                                selectionSet: {
                                                  kind: "SelectionSet",
                                                  selections: [{
                                                    kind: "Field",
                                                    name: {
                                                      kind: "Name",
                                                      value: "value"
                                                    }
                                                  }]
                                                }
                                              }, {
                                                kind: "Field",
                                                name: {
                                                  kind: "Name",
                                                  value: "lastname"
                                                },
                                                selectionSet: {
                                                  kind: "SelectionSet",
                                                  selections: [{
                                                    kind: "Field",
                                                    name: {
                                                      kind: "Name",
                                                      value: "value"
                                                    }
                                                  }]
                                                }
                                              }]
                                            }
                                          }]
                                        }
                                      }]
                                    }
                                  }]
                                }
                              }]
                            }
                          }, {
                            kind: "Field",
                            name: {
                              kind: "Name",
                              value: "id"
                            }
                          }, {
                            kind: "Field",
                            name: {
                              kind: "Name",
                              value: "defaultProperties"
                            },
                            selectionSet: {
                              kind: "SelectionSet",
                              selections: [{
                                kind: "Field",
                                name: {
                                  kind: "Name",
                                  value: "hs_thread_id"
                                },
                                selectionSet: {
                                  kind: "SelectionSet",
                                  selections: [{
                                    kind: "Field",
                                    name: {
                                      kind: "Name",
                                      value: "value"
                                    }
                                  }, {
                                    kind: "Field",
                                    name: {
                                      kind: "Name",
                                      value: "id"
                                    }
                                  }]
                                }
                              }, {
                                kind: "Field",
                                name: {
                                  kind: "Name",
                                  value: "hs_conversation_channel"
                                },
                                selectionSet: {
                                  kind: "SelectionSet",
                                  selections: [{
                                    kind: "Field",
                                    name: {
                                      kind: "Name",
                                      value: "id"
                                    }
                                  }, {
                                    kind: "Field",
                                    name: {
                                      kind: "Name",
                                      value: "value"
                                    }
                                  }]
                                }
                              }, {
                                kind: "Field",
                                name: {
                                  kind: "Name",
                                  value: "hs_name"
                                },
                                selectionSet: {
                                  kind: "SelectionSet",
                                  selections: [{
                                    kind: "Field",
                                    name: {
                                      kind: "Name",
                                      value: "id"
                                    }
                                  }, {
                                    kind: "Field",
                                    name: {
                                      kind: "Name",
                                      value: "value"
                                    }
                                  }]
                                }
                              }, {
                                kind: "Field",
                                name: {
                                  kind: "Name",
                                  value: "hs_inbox_id"
                                },
                                selectionSet: {
                                  kind: "SelectionSet",
                                  selections: [{
                                    kind: "Field",
                                    name: {
                                      kind: "Name",
                                      value: "id"
                                    }
                                  }, {
                                    kind: "Field",
                                    name: {
                                      kind: "Name",
                                      value: "value"
                                    }
                                  }]
                                }
                              }, {
                                kind: "Field",
                                name: {
                                  kind: "Name",
                                  value: "hs_originating_channel_instance_id"
                                },
                                selectionSet: {
                                  kind: "SelectionSet",
                                  selections: [{
                                    kind: "Field",
                                    name: {
                                      kind: "Name",
                                      value: "id"
                                    }
                                  }, {
                                    kind: "Field",
                                    name: {
                                      kind: "Name",
                                      value: "value"
                                    }
                                  }]
                                }
                              }, {
                                kind: "Field",
                                name: {
                                  kind: "Name",
                                  value: "hs_started_at"
                                },
                                selectionSet: {
                                  kind: "SelectionSet",
                                  selections: [{
                                    kind: "Field",
                                    name: {
                                      kind: "Name",
                                      value: "id"
                                    }
                                  }, {
                                    kind: "Field",
                                    name: {
                                      kind: "Name",
                                      value: "value"
                                    }
                                  }]
                                }
                              }, {
                                kind: "Field",
                                name: {
                                  kind: "Name",
                                  value: "hs_last_message_at"
                                },
                                selectionSet: {
                                  kind: "SelectionSet",
                                  selections: [{
                                    kind: "Field",
                                    name: {
                                      kind: "Name",
                                      value: "id"
                                    }
                                  }, {
                                    kind: "Field",
                                    name: {
                                      kind: "Name",
                                      value: "value"
                                    }
                                  }]
                                }
                              }]
                            }
                          }]
                        }
                      }]
                    }
                  }]
                }
              }]
            }
          }]
        }
      }]
    }
  }]
});